import React, { useState, useEffect } from 'react';
import { X, Printer, Download, Mail, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../../services/api';
import { getDeviseCourante } from '../../utils/format';
import BonCommandeA4 from './BonCommandeA4';
import DocumentImprimable, { SelecteurFormat } from '../../components/common/DocumentImprimable';
import useFormatImpression from '../../components/common/useFormatImpression';

// Génère un PDF NATIF du bon de commande (texte sélectionnable, ~10 ko).
// Utilise jsPDF + jspdf-autotable plutôt que html2canvas+image — plus
// léger, plus net, et sélectionnable côté lecteur PDF.
function genererPDF(bon, parametres = {}) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageW = doc.internal.pageSize.getWidth();   // 210
  let y = 14;

  const nomEntreprise = parametres.nom_entreprise || 'Mon entreprise';
  const adresse       = parametres.adresse || '';
  const ville         = parametres.ville || 'Conakry, Guinée';
  const telephoneEnt  = parametres.telephone || '';
  const emailEnt      = parametres.email || '';

  // ── En-tête entreprise (gauche)
  doc.setFont('helvetica', 'bold').setFontSize(14);
  doc.text(nomEntreprise, 14, y);
  doc.setFont('helvetica', 'normal').setFontSize(9);
  let yLeft = y + 5;
  if (adresse)      { doc.text(adresse, 14, yLeft); yLeft += 4; }
  if (ville)        { doc.text(ville, 14, yLeft); yLeft += 4; }
  if (telephoneEnt) { doc.text(`Tél : ${telephoneEnt}`, 14, yLeft); yLeft += 4; }
  if (emailEnt)     { doc.text(`Email : ${emailEnt}`, 14, yLeft); yLeft += 4; }

  // ── Titre + métadonnées (droite)
  doc.setFont('helvetica', 'bold').setFontSize(16);
  doc.text('BON DE COMMANDE', pageW - 14, y, { align: 'right' });
  doc.setFont('helvetica', 'normal').setFontSize(9);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
  doc.text(`N° : ${bon.numero}`, pageW - 14, y + 6, { align: 'right' });
  doc.text(`Émis le : ${fmtDate(bon.date_emission)}`, pageW - 14, y + 10, { align: 'right' });
  doc.text(`Livraison : ${bon.date_livraison_prevue ? fmtDate(bon.date_livraison_prevue) : 'À convenir'}`,
           pageW - 14, y + 14, { align: 'right' });

  y = Math.max(yLeft, y + 18);
  doc.setDrawColor(0).setLineWidth(0.4);
  doc.line(14, y, pageW - 14, y);
  y += 6;

  // ── Bloc fournisseur + émetteur (deux colonnes)
  const colW = (pageW - 28 - 4) / 2; // 4mm de gap au milieu
  doc.setDrawColor(180).setLineWidth(0.2);
  // Fournisseur
  doc.rect(14, y, colW, 24);
  doc.setFont('helvetica', 'bold').setFontSize(7);
  doc.setTextColor(100);
  doc.text('FOURNISSEUR', 16, y + 4);
  doc.setTextColor(0).setFontSize(10);
  doc.text(bon.fournisseur_nom || '—', 16, y + 9);
  doc.setFontSize(8).setFont('helvetica', 'normal').setTextColor(80);
  let yF = y + 13;
  if (bon.fournisseur_specialite) { doc.text(`Spécialité : ${bon.fournisseur_specialite}`, 16, yF); yF += 4; }
  if (bon.fournisseur_adresse)    { doc.text(bon.fournisseur_adresse, 16, yF); yF += 4; }
  if (bon.fournisseur_telephone)  { doc.text(`Tél : ${bon.fournisseur_telephone}`, 16, yF); yF += 4; }
  // Émetteur
  const x2 = 14 + colW + 4;
  doc.rect(x2, y, colW, 24);
  doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(100);
  doc.text('COMMANDE ÉMISE PAR', x2 + 2, y + 4);
  doc.setTextColor(0).setFontSize(10);
  doc.text(bon.emetteur_nom || '—', x2 + 2, y + 9);
  doc.setFontSize(8).setFont('helvetica', 'normal').setTextColor(80);
  let yE = y + 13;
  if (bon.emetteur_role) { doc.text(`Rôle : ${bon.emetteur_role}`, x2 + 2, yE); yE += 4; }
  doc.text('Devise : ' + getDeviseCourante(), x2 + 2, yE); yE += 4;
  doc.text('Mode de règlement : à préciser', x2 + 2, yE);
  doc.setTextColor(0);

  y += 30;

  // ── Tableau articles via autotable
  const body = (bon.lignes || []).map((l, i) => {
    const qte = Number(l.quantite || 0);
    const pu  = Number(l.prix_achat_unitaire || 0);
    const st  = Number(l.sous_total || qte * pu);
    const nom = [l.produit_nom, l.modele, l.taille_nom, l.couleur && `(${l.couleur})`]
      .filter(Boolean).join(' ');
    return [
      String(i + 1),
      nom,
      l.code_variante || '—',
      String(qte),
      pu.toLocaleString('fr-FR'),
      st.toLocaleString('fr-FR'),
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [['#', 'Désignation', 'Réf.', 'Qté', `P.U. (${getDeviseCourante()})`, `Total (${getDeviseCourante()})`]],
    body,
    margin: { left: 14, right: 14 },
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [240, 240, 240], textColor: 0, fontStyle: 'bold' },
    columnStyles: {
      0: { halign: 'center', cellWidth: 8 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 24, font: 'courier', fontSize: 8 },
      3: { halign: 'center', cellWidth: 12 },
      4: { halign: 'right', cellWidth: 26 },
      5: { halign: 'right', cellWidth: 30, fontStyle: 'bold' },
    },
  });

  let y2 = doc.lastAutoTable.finalY + 6;

  // ── Totaux à droite
  const nbArticles = (bon.lignes || []).reduce((s, l) => s + Number(l.quantite || 0), 0);
  const xT = pageW - 14 - 70;
  doc.setFontSize(9).setFont('helvetica', 'normal').setTextColor(80);
  doc.text("Nombre d'articles", xT, y2);
  doc.setTextColor(0);
  doc.text(`${nbArticles} pièces`, pageW - 14, y2, { align: 'right' });
  y2 += 5;
  doc.setTextColor(80);
  doc.text('Sous-total', xT, y2);
  doc.setTextColor(0);
  doc.text(`${Number(bon.cout_total || 0).toLocaleString('fr-FR')} ${getDeviseCourante()}`, pageW - 14, y2, { align: 'right' });
  y2 += 3;
  doc.setLineWidth(0.5).line(xT, y2, pageW - 14, y2);
  y2 += 5;
  doc.setFont('helvetica', 'bold').setFontSize(11);
  doc.text('TOTAL À PAYER', xT, y2);
  doc.text(`${Number(bon.cout_total || 0).toLocaleString('fr-FR')} ${getDeviseCourante()}`, pageW - 14, y2, { align: 'right' });
  y2 += 8;

  // ── Notes
  doc.setDrawColor(180).setLineWidth(0.2);
  doc.rect(14, y2, pageW - 28, 16);
  doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(100);
  doc.text('NOTES / CONDITIONS', 16, y2 + 4);
  doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(80);
  const notesText = bon.notes || 'Livraison à confirmer. Marchandise contrôlée à l\'arrivée.';
  const notesLines = doc.splitTextToSize(notesText, pageW - 32);
  doc.text(notesLines, 16, y2 + 9);
  doc.setTextColor(0);
  y2 += 22;

  // ── Signatures
  const sigW = (pageW - 28 - 12) / 2;
  doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(100);
  doc.text(`POUR ${nomEntreprise.toUpperCase()}`, 14, y2);
  doc.text('BON POUR ACCORD — FOURNISSEUR', 14 + sigW + 12, y2);
  y2 += 18;
  doc.setLineWidth(0.4).setDrawColor(0);
  doc.line(14, y2, 14 + sigW, y2);
  doc.line(14 + sigW + 12, y2, pageW - 14, y2);
  doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(120);
  doc.text('Nom · Date · Signature', 14, y2 + 4);
  doc.text('Nom · Date · Signature & cachet', 14 + sigW + 12, y2 + 4);

  // ── Footer
  const footerY = doc.internal.pageSize.getHeight() - 8;
  doc.setFontSize(8).setTextColor(150);
  doc.text(
    `${nomEntreprise} · Document généré le ${fmtDate(new Date())} · Réf ${bon.numero}`,
    pageW / 2, footerY, { align: 'center' }
  );

  return doc;
}

export default function ModalImprimerBon({ bon, onClose }) {
  const [parametres, setParametres] = useState({});
  const [format, setFormat] = useFormatImpression();

  useEffect(() => {
    // Récupère les paramètres entreprise pour l'en-tête. Si l'utilisateur
    // n'est pas autorisé (employé qui aurait accès à un bon), on tolère
    // l'absence et on utilise les valeurs par défaut.
    api.get('/parametres').then(r => setParametres(r.data || {})).catch(() => {});
  }, []);

  const handleImprimer = () => window.print();

  const handleDownloadPDF = () => {
    try {
      const doc = genererPDF(bon, parametres);
      doc.save(`${bon.numero}.pdf`);
    } catch (err) {
      console.error('Erreur génération PDF:', err);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const handleEmail = async () => {
    if (!bon.fournisseur_email) {
      toast.error('Le fournisseur n\'a pas d\'email enregistré. Ajoute-le sur sa fiche.');
      return;
    }
    try {
      const doc = genererPDF(bon, parametres);
      const pdfBase64 = doc.output('datauristring').split(',')[1]; // base64 brut
      await api.post(`/bons-commande/${bon.id}/email`, {
        pdf_base64: pdfBase64,
        filename: `${bon.numero}.pdf`,
      });
      toast.success(`Email envoyé à ${bon.fournisseur_email}`);
    } catch (err) {
      const msg = err.response?.data?.message || 'Erreur envoi email';
      toast.error(msg);
    }
  };

  const handleWhatsApp = () => {
    const tel = (bon.fournisseur_telephone || '').replace(/[^\d]/g, '');
    if (!tel) {
      toast.error('Le fournisseur n\'a pas de téléphone enregistré.');
      return;
    }
    const total = Number(bon.cout_total || 0).toLocaleString('fr-FR');
    const msg = `Bonjour,\n\nVoici notre bon de commande ${bon.numero} (${total} ${getDeviseCourante()}). Le PDF est en pièce jointe.\n\nMerci,\n${parametres.nom_entreprise || 'Mon entreprise'}`;
    const url = `https://wa.me/${tel}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    toast('Pense à attacher le PDF dans WhatsApp', { icon: '📎' });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 overflow-y-auto print:p-0 print:bg-white">
      <div className="bg-white rounded-xl w-full max-w-3xl my-4 shadow-2xl print:shadow-none print:max-w-none print:my-0 print:rounded-none">
        {/* Barre d'actions — masquée à l'impression */}
        <div className="flex flex-wrap gap-2 p-4 border-b border-gray-200 print:hidden">
          <button onClick={handleImprimer}
            className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black font-bold px-4 py-2 rounded-lg transition-colors">
            <Printer size={16} /> Imprimer
          </button>
          <button onClick={handleDownloadPDF}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-4 py-2 rounded-lg transition-colors">
            <Download size={16} /> Télécharger PDF
          </button>
          <button onClick={handleEmail}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-bold px-4 py-2 rounded-lg transition-colors">
            <Mail size={16} /> Envoyer par email
          </button>
          <button onClick={handleWhatsApp}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white font-bold px-4 py-2 rounded-lg transition-colors">
            <MessageCircle size={16} /> WhatsApp
          </button>
          <div className="ml-auto flex items-center gap-2">
            <SelecteurFormat format={format} onChange={setFormat} />
            <button onClick={onClose}
              className="flex items-center gap-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold px-4 py-2 rounded-lg transition-colors">
              <X size={16} /> Fermer
            </button>
          </div>
        </div>

        {/* Aperçu — A4 (modèle complet) ou rouleau thermique */}
        <div className="p-4 bg-gray-100 print:p-0 print:bg-white">
          {format === 'a4' ? (
            <BonCommandeA4 bon={bon} parametres={parametres} />
          ) : (
            <div className="bg-white mx-auto" style={{ maxWidth: '74mm' }}>
              <DocumentImprimable
                format="thermique"
                titre="BON DE COMMANDE"
                sousTitre={`Fournisseur : ${bon.fournisseur_nom || '—'}`}
                date={`Émis le ${bon.date_emission ? new Date(bon.date_emission).toLocaleDateString('fr-FR') : '—'}`}
                reference={`N° ${bon.numero}`}
                lignes={(bon.lignes || []).map(l => ({
                  nom: [l.produit_nom, l.modele, l.taille_nom, l.couleur && `(${l.couleur})`].filter(Boolean).join(' '),
                  qte: Number(l.quantite || 0),
                  pu: Number(l.prix_achat_unitaire || 0),
                  total: Number(l.sous_total ?? (Number(l.quantite || 0) * Number(l.prix_achat_unitaire || 0))),
                }))}
                total={bon.cout_total}
                piedMessage={bon.notes || 'Livraison à confirmer.'}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
